import { useMemo, useState } from 'react';
import type { Domain } from '@lizrd/contracts';
import { Checkbox } from './ui/checkbox';
import { Input } from './ui/input';

/**
 * A reusable multi-select list of domains backed by checkboxes — the shared
 * primitive behind both the Members-page "Manage domains" modal and the
 * assign-at-invite pickers. `value` is the set of selected domain ids; `onChange`
 * gets the full next set. Search only appears past a small threshold (the app
 * caps domains low, so most orgs never see it).
 */
export function DomainAccessChecklist({
  domains,
  value,
  onChange,
  disabled,
  emptyLabel = 'No domains yet.',
}: {
  domains: Domain[];
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  emptyLabel?: string;
}) {
  const [q, setQ] = useState('');
  const selected = useMemo(() => new Set(value), [value]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return domains;
    return domains.filter((d) => d.name.toLowerCase().includes(needle));
  }, [domains, q]);

  const toggle = (id: string, on: boolean) => {
    const next = new Set(selected);
    if (on) next.add(id);
    else next.delete(id);
    onChange([...next]);
  };

  if (domains.length === 0) {
    return <p className="text-sm text-muted">{emptyLabel}</p>;
  }

  return (
    <div className="space-y-2">
      {domains.length > 8 && (
        <Input
          placeholder="Search domains…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="w-full"
        />
      )}
      <div className="max-h-64 divide-y divide-hairline overflow-y-auto rounded-md border border-hairline">
        {filtered.length === 0 ? (
          <p className="p-3 text-sm text-muted">No domains match your search.</p>
        ) : (
          filtered.map((d) => (
            <div key={d.id} className="px-3 py-2.5">
              <Checkbox
                checked={selected.has(d.id)}
                disabled={disabled}
                onChange={(e) => toggle(d.id, e.target.checked)}
                label={d.name}
                description={d.status !== 'ACTIVE' ? d.status.toLowerCase() : undefined}
              />
            </div>
          ))
        )}
      </div>
    </div>
  );
}
